// Skinned-mesh inverted-hull outline pipeline. Opt-in only - nothing wires
// this in automatically. A scene object that already owns a
// SkinnedRenderPipeline (e.g. via Character/CharacterRuntime) constructs
// one of these alongside it and reuses that pipeline's already-built
// `Mesh` (see build_from_mesh()) - no second glTF load, no second
// vertex/index GPUBuffer upload, uint16/uint32 index format preserved
// exactly.
//
// Joint-palette lifecycle: this pipeline does NOT call
// AnimationControllerGpu.acquire_latest_joint_palette() itself. The
// AnimationPool backing that lease supports exactly one concurrent lease
// per acquire/release pair (a second acquire() while one is already held
// returns null - see AnimationPool.acquire_latest_ready_slot), so a
// second acquire here would either silently fail or, if it somehow
// succeeded, would let the outline mesh skin against a *different* pool
// slot than the base mesh drew with, producing visible skinning drift
// between the character and its outline. Instead, prepare() must be
// called AFTER the paired SkinnedRenderPipeline.prepare() for the same
// frame, and reads that pipeline's already-acquired palette buffer via
// SkinnedRenderPipeline.get_current_joint_palette(). Release still
// happens exactly once, from the base SkinnedRenderPipeline.finish_draw()
// - this pipeline's own finish_draw() is a no-op release-wise (it just
// drops its bind group reference) so there is no double-release either.
//
// Draw order: the owning scene object must draw the base
// SkinnedRenderPipeline first, then this pipeline, in the same render
// pass - identical contract to StaticOutlineRenderPipeline.

import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import Mesh from "../../geometry/mesh";
import Transform from "../transform";
import SkinnedRenderPipeline from "./skinned_renderpipeline";
import {
  OutlineState,
  DEFAULT_OUTLINE_WIDTH,
  DEFAULT_OUTLINE_COLOR,
  OUTLINE_PRIMITIVE_STATE,
  OUTLINE_DEPTH_STENCIL_STATE,
} from "./outline_state";
import { RENDER_SAMPLE_COUNT } from "../render_config";

export default class SkinnedOutlineRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;

  camera_buffer: GPUBuffer | null = null;
  object_buffer: GPUBuffer | null = null;
  per_frame_bind_group: GPUBindGroup | null = null;

  // Not owned - a reference to the base SkinnedRenderPipeline's
  // already-built Mesh. Never call mesh.build() on this from here.
  mesh: Mesh | null = null;

  private outline_state: OutlineState;

  constructor(
    key: string,
    shader_name: string,
    width: number = DEFAULT_OUTLINE_WIDTH,
    color: readonly [number, number, number, number] = DEFAULT_OUTLINE_COLOR
  ) {
    super(key + "_skinned_outline");
    this.label = key + "_skinned_outline";
    this.shader_name = shader_name;
    this.outline_state = new OutlineState(width, color);
  }

  /** Outline extrusion width, in world units. */
  set_line_width(width: number): void {
    this.outline_state.set_line_width(width);
  }

  /** Flat outline color. Defaults to opaque black. */
  set_line_color(r: number, g: number, b: number, a: number): void {
    this.outline_state.set_line_color(r, g, b, a);
  }

  /**
   * Builds the outline GPU pipeline and binds it to an already-built Mesh
   * owned by a sibling SkinnedRenderPipeline. Does not call mesh.build()
   * and does not allocate any new vertex/index GPUBuffer.
   */
  build_from_mesh(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    mesh: Mesh
  ): void {
    this.mesh = mesh;

    const shader = shader_service.get_render(this.shader_name);

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [
          {
            // Matches SkinnedRenderPipeline's vertex layout exactly.
            arrayStride: 64,
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x2" },
              { shaderLocation: 3, offset: 32, format: "uint32x4" },
              { shaderLocation: 4, offset: 48, format: "float32x4" },
            ],
          },
        ],
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [{ format }],
      },
      primitive: OUTLINE_PRIMITIVE_STATE,
      depthStencil: OUTLINE_DEPTH_STENCIL_STATE,
      multisample: {
        count: RENDER_SAMPLE_COUNT,
      },
    });

    this.camera_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.object_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.outline_state.build(device, this.label);
  }

  /**
   * Must be called AFTER base_pipeline.prepare() has run for this frame,
   * so base_pipeline.get_current_joint_palette() reflects this frame's
   * lease. Does not acquire or release any AnimationPool lease itself -
   * it only reads the buffer reference the base pipeline already leased.
   * If the base pipeline has no palette this frame (e.g. no animation
   * slot ready yet), this pipeline skips building a bind group and
   * draw() below becomes a no-op for this frame, matching how
   * SkinnedRenderPipeline itself behaves in that case.
   */
  prepare(
    device: GPUDevice,
    camera: Camera,
    transform: Transform,
    base_pipeline: SkinnedRenderPipeline
  ): void {
    if (!this.camera_buffer || !this.object_buffer || !this.pipeline) return;

    const palette_buffer = base_pipeline.get_current_joint_palette();
    if (!palette_buffer) {
      this.per_frame_bind_group = null;
      return;
    }

    device.queue.writeBuffer(
      this.camera_buffer,
      0,
      camera.view_projection_matrix.buffer as ArrayBuffer,
      camera.view_projection_matrix.byteOffset,
      camera.view_projection_matrix.byteLength
    );

    device.queue.writeBuffer(
      this.object_buffer,
      0,
      transform.model_matrix.buffer as ArrayBuffer,
      transform.model_matrix.byteOffset,
      transform.model_matrix.byteLength
    );

    this.outline_state.write(device);

    this.per_frame_bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label + "_cam_obj_joint_outline_bind_group",
      entries: [
        { binding: 0, resource: { buffer: this.camera_buffer } },
        { binding: 1, resource: { buffer: this.object_buffer } },
        { binding: 2, resource: { buffer: palette_buffer } },
        { binding: 3, resource: { buffer: this.outline_state.outline_buffer! } },
      ],
    });
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.per_frame_bind_group || !this.mesh) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.per_frame_bind_group);
    this.mesh.draw(pass);
  }

  /**
   * No-op with respect to the joint-palette lease (this pipeline never
   * acquired one) - only drops this frame's bind group reference. The
   * base SkinnedRenderPipeline.finish_draw() remains the single place
   * that releases the lease, exactly once, after both this pipeline's
   * draw() and the base pipeline's draw() have both run.
   */
  finish_draw(): void {
    this.per_frame_bind_group = null;
  }
}
